import { PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID } from '@billing/validation/tariff';
import { MRT_ColumnFiltersState, MRT_SortingState } from 'mantine-react-table';
import {
  formatDateFilterToken,
  isDateRangeFilterID,
  normalizeDateRangeFilter,
} from 'utils/dateUtils';

export const isMeaningfulFilterValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return false;
  if (typeof value === 'string') return Boolean(value.trim());
  if (Array.isArray(value)) return value.some(isMeaningfulFilterValue);
  return true;
};

export const scrubDateFilter = (filters: any[]) => {
  if (!filters || !Array.isArray(filters)) return [];

  return filters.map((filter) => {
    if (!isDateRangeFilterID(filter.id) || !Array.isArray(filter.value)) {
      return filter;
    }
    return normalizeDateRangeFilter(filter);
  });
};

export const areColumnFiltersEqual = (
  left: MRT_ColumnFiltersState = [],
  right: MRT_ColumnFiltersState = []
) =>
  JSON.stringify(scrubDateFilter(left)) ===
  JSON.stringify(scrubDateFilter(right));

export const cloneColumnFilters = (
  filters: MRT_ColumnFiltersState = []
): MRT_ColumnFiltersState =>
  filters.map((filter) => ({
    ...filter,
    value: Array.isArray(filter.value) ? [...filter.value] : filter.value,
  }));

/**
 * Cleans the provided column filters by removing any filters with empty or null values for the 'createdAt' column,
 * and converts the date values to UTC format (YYYY-MM-DD) joined by '~'.
 *
 * @param columnFilters - The column filters to be cleaned
 * @interface MRT_ColumnFiltersState - { id: string, value: string | number | boolean | Date | Date[] }[]
 * @returns An array of cleaned column filters
 */
const processColumnFilters = (columnFilters: MRT_ColumnFiltersState) => {
  if (!columnFilters) return [];

  return columnFilters?.reduce((acc, filter) => {
    switch (filter.id) {
      case PROVIDER_DASHBOARD_BILLED_DATE_FILTER_ID:
      case 'serviceStart':
      case 'claim.serviceStart':
      case 'createdAt':
      case 'submissions.submittedAt':
      case 'remittances.parsedAt':
      case 'claims.serviceStart':
        if (Array.isArray(filter.value)) {
          const [startDate, endDate] = filter.value;
          const start = formatDateFilterToken(startDate);
          const end = formatDateFilterToken(endDate);
          if (start && end) acc.push({ ...filter, value: `${start}~${end}` });
        }
        break;
      default:
        if (Array.isArray(filter.value)) {
          const nonEmptyValues = filter.value.filter(
            (value) => value !== undefined && value !== null && value !== ''
          );
          if (nonEmptyValues.length > 0) {
            acc.push({ ...filter, value: nonEmptyValues });
          }
          break;
        }
        if (
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== ''
        ) {
          acc.push(filter);
        }
        break;
    }
    return acc;
  }, [] as MRT_ColumnFiltersState);
};

/**
 * Converts filters to a query string.
 *
 * @param filters - The filters to convert.
 * @delimiters {string} -
 **',' is used to separate filters,
 **':' is for key value pairs,
 **'~' is used for date ranges.
 * @returns The query string representation of the filters.
 */
export const convertFiltersToQueryString = (
  filters: MRT_ColumnFiltersState,
  encodeValues = false
) => {
  const validFilters = processColumnFilters(filters);
  const serializeValue = (value: unknown) => {
    const text = String(value);
    return encodeValues ? encodeURIComponent(text) : text;
  };
  return validFilters
    .flatMap((filter) => {
      if (Array.isArray(filter.value)) {
        return filter.value.map(
          (value) => `${filter.id}:${serializeValue(value)}`
        );
      }
      return [`${filter.id}:${serializeValue(filter.value)}`];
    })
    .join(',');
};

// Claim filters encode each value before joining; the backend splits on literal
// commas and colons. Keep this separate from callers that use unencoded values.
export const convertClaimFiltersToQueryString = (
  filters: MRT_ColumnFiltersState
) => convertFiltersToQueryString(filters, true);

export const convertGlobalFilterToQueryString = (
  globalFilter: string | undefined
) => {
  return globalFilter ? `${globalFilter}` : '';
};

export const convertSortingToQueryString = (sorting: MRT_SortingState) => {
  return sorting.map((sort) => `${sort.id}:${sort.desc}`).join(',');
};
